import { NavLink } from "react-router-dom";
import { useState } from "react";
import { BrandMark } from "./Brand";
import { Hamburger, Help, Logout } from "./Icons";
import { useData } from "../data/DataContext";
import { ESTATES, OPERATIONS, PLATFORM } from "../nav";
import { useAuth } from "../auth/AuthContext";
import { OncallWidget } from "./OncallWidget";
import { useUI } from "../data/UIContext";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: Props) {
  const data = useData();
  const auth = useAuth();
  const ui = useUI();
  const [lang, setLang] = useState<"en" | "ar">("en");

  const toggleLang = (next: "en" | "ar") => {
    setLang(next);
    document.documentElement.lang = next;
    document.documentElement.dir = next === "ar" ? "rtl" : "ltr";
  };

  const stats = data.commandCenter?.stats;
  const totalGaps = data.coverage
    ? Object.values(data.coverage.gaps || {}).reduce((a, b) => a + b, 0)
    : 0;

  const counts: Record<string, string | number> = {
    backend:      stats?.backend_total ?? "·",
    surfaces:     stats?.surfaces_total ?? "·",
    integrations: stats?.integrations_total ?? "·",
    clients:      stats?.clients_total ?? "·",
    coverage:     totalGaps,
    scorecard:    "·",
    slo:          "·",   // live count would require loading SLO budgets globally; tab owns it
    infra:        "·",
    logs:         "·",
    "alert-health": "·",
    incidents:    "·",   // populated by IncidentsTab when it runs
    cicd:         "·",
    dashboards:   "·",
    dora:         data.dora?.overall?.deploys_total ?? "·",
    graph: ((data.commandCenter?.estates.backend.length ?? 0) +
            (data.commandCenter?.estates.surfaces.length ?? 0) +
            (data.commandCenter?.estates.integrations.length ?? 0)),
    tools:        data.links?.links.length ?? "·",
  };

  return (
    <aside className={`sidebar${open ? " open" : ""}`}>
      <div className="brand">
        <a href="/" className="brand-link" aria-label="Waffy Command Center">
          <BrandMark />
          <span className="brand-text">
            <span className="brand-name">Waffy</span>
            <span className="brand-sub">Command Center</span>
          </span>
        </a>
        <button className="sidebar-toggle" onClick={onClose} aria-label="Close navigation">
          <Hamburger size={18} />
        </button>
      </div>

      <OncallWidget />

      <nav className="nav" role="navigation">
        <div className="nav-section">Estates</div>
        {ESTATES.map((it) => (
          <NavItem key={it.id} item={it} count={counts[it.id]} onNavigate={onClose} />
        ))}
        <div className="nav-section">Operations</div>
        {OPERATIONS.map((it) => {
          if (it.id === "tools" && !data.links) return null;
          if (it.id === "coverage" && !data.coverage) return null;
          let countCls = "";
          if (it.id === "coverage") {
            countCls = totalGaps > 30 ? " crit" : totalGaps > 0 ? " warn" : "";
          }
          return <NavItem key={it.id} item={it} count={counts[it.id]} countCls={countCls} onNavigate={onClose} />;
        })}
        <div className="nav-section">Platform</div>
        {PLATFORM.map((it) => (
          <NavItem key={it.id} item={it} count={counts[it.id]} onNavigate={onClose} />
        ))}
      </nav>

      <div className="sidebar-foot">
        <div className="lang-toggle" role="group" aria-label="Language">
          <button className={`lang-btn${lang === "en" ? " active" : ""}`}
                  onClick={() => toggleLang("en")} type="button">EN</button>
          <button className={`lang-btn${lang === "ar" ? " active" : ""}`}
                  onClick={() => toggleLang("ar")} type="button"
                  title="Arabic translations coming soon">العربية</button>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="help-btn" type="button"
                  title="Keyboard shortcuts (?)"
                  onClick={() => ui.setHelpOpen(true)}>
            <Help size={14} />
            <span>Shortcuts</span>
          </button>
          <button className="help-btn" type="button"
                  title={`Sign out${auth.state.status === "authenticated" ? " (" + auth.state.user + ")" : ""}`}
                  onClick={() => void auth.logout()}>
            <Logout size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
}

interface NavItemProps {
  item: typeof ESTATES[number];
  count?: string | number;
  countCls?: string;
  onNavigate: () => void;
}

function NavItem({ item, count, countCls, onNavigate }: NavItemProps) {
  return (
    <NavLink to={item.path}
      className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
      onClick={onNavigate}>
      <item.Icon className="nav-icon" />
      <span className="nav-label">{item.label}</span>
      {count != null && <span className={`nav-count${countCls ?? ""}`} data-count-for={item.id}>{count}</span>}
    </NavLink>
  );
}
